var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { backendUrl, SESSION_TOKEN_HEADER, USER_NAME_HEADER } from '@common/contracts';
import { getSessionToken } from '../auth/session';
import { getIdentity } from '../auth/identity';
import { client } from './client';
import { newProgressId } from './progress';
import { pollJob } from './jobs';
// Stats for the admin Reset DB dialog (safe, read-only).
export function getResetDatabaseStats() {
    return client.get('/dev/reset-database');
}
// FULL RESET: wipes every non-demo level and all its content on the server.
// Admin-only, and refused unless the code-only deploy switch allows it.
export function resetDatabase() {
    return client.post('/dev/reset-database', {});
}
/**
 * Download a level bundle and hand it to the browser as a file.
 *
 * A plain fetch rather than the axios client: this response is a zip, and the
 * client's interceptor is built to unwrap JSON bodies. The filename comes from
 * the server's Content-Disposition, which is where it belongs — the server names
 * the file after the level and the day it was taken.
 */
export function downloadLevelBundle(levelId, levelName) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const headers = {};
        const token = getSessionToken();
        if (token)
            headers[SESSION_TOKEN_HEADER] = token;
        const identity = getIdentity();
        if (identity.username)
            headers[USER_NAME_HEADER] = identity.username;
        const res = yield fetch(`${backendUrl}/api/levels/${levelId}/bundle`, { headers });
        if (!res.ok) {
            const body = yield res.json().catch(() => null);
            throw new Error((body === null || body === void 0 ? void 0 : body.message) || `Export failed (${res.status})`);
        }
        const disposition = (_a = res.headers.get('Content-Disposition')) !== null && _a !== void 0 ? _a : '';
        const named = (_b = /filename="([^"]+)"/.exec(disposition)) === null || _b === void 0 ? void 0 : _b[1];
        const slug = levelName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'level';
        const url = URL.createObjectURL(yield res.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = named || `level-${slug}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    });
}
export function inspectLevelBundle(file) {
    const form = new FormData();
    form.append('bundle', file);
    return client.post('/db/level-bundle/inspect', form, { timeout: 10 * 60 * 1000 });
}
export function applyLevelBundle(file, force = false) {
    const form = new FormData();
    form.append('bundle', file);
    if (force)
        form.append('force', 'true');
    return client.post('/db/level-bundle', form, { timeout: 10 * 60 * 1000 });
}
export function getActionPatchTargets() {
    return client.get('/db/patch/actions');
}
/** Long job: answers 202 and reports through the progress registry. */
export function runActionPatch(levelIds, onProgress) {
    return __awaiter(this, void 0, void 0, function* () {
        const progressId = newProgressId();
        yield client.post('/db/patch/actions', { levelIds, progressId });
        return pollJob(progressId, 'The repair lost contact with the server. Rescan to see what was done.', onProgress);
    });
}
/** Download the whole database as a zip. Plain fetch: the body is an archive,
 *  and the axios client is built to unwrap JSON. */
export function downloadDbBundle() {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const headers = {};
        const token = getSessionToken();
        if (token)
            headers[SESSION_TOKEN_HEADER] = token;
        const identity = getIdentity();
        if (identity.username)
            headers[USER_NAME_HEADER] = identity.username;
        const res = yield fetch(`${backendUrl}/api/db/export`, { headers });
        if (!res.ok) {
            const body = yield res.json().catch(() => null);
            throw new Error((body === null || body === void 0 ? void 0 : body.message) || `Export failed (${res.status})`);
        }
        const named = (_b = /filename="([^"]+)"/.exec((_a = res.headers.get('Content-Disposition')) !== null && _a !== void 0 ? _a : '')) === null || _b === void 0 ? void 0 : _b[1];
        const url = URL.createObjectURL(yield res.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = named || 'database.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    });
}
export function inspectDbBundle(file) {
    const form = new FormData();
    form.append('bundle', file);
    return client.post('/db/import/inspect', form, { timeout: 30 * 60 * 1000 });
}
/** Destructive: replaces this database with the bundle's contents. Long job —
 *  answers 202 and reports through the progress registry. */
export function applyDbBundle(file, onProgress) {
    return __awaiter(this, void 0, void 0, function* () {
        const progressId = newProgressId();
        const form = new FormData();
        form.append('bundle', file);
        form.append('confirm', 'REPLACE');
        form.append('progressId', progressId);
        yield client.post('/db/import', form, { timeout: 30 * 60 * 1000 });
        return pollJob(progressId, 'The import lost contact with the server. Reopen this dialog to see what landed.', onProgress);
    });
}
